import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type { StepType } from '../../../storefront/types';
import type { Plugin } from '../types';

/**
 * Installation context that can be provided from different sources:
 * - Step Editor: funnelId + stepType already known
 * - Marketplace: User selects during wizard
 * - Direct: Programmatic installation
 */
interface InstallationContext {
  funnelId?: string;
  stepType?: StepType;
  mode: 'new-funnel' | 'existing-funnel' | 'direct';
}

interface PluginInstallationState {
  // Current plugin being installed
  plugin: Plugin | null;
  selectedDeploymentId: string | null;
  selectedPreset: string | null;

  // Installation context
  context: InstallationContext | null;

  // Wizard state
  wizardOpen: boolean;
  configEditorOpen: boolean;
  configEditorInstanceId: string | null;

  // Navigation state
  targetFunnelId: string | null;
  shouldNavigateOnClose: boolean;

  // Session tracking - changes with each new installation
  sessionId: string;
}

interface PluginInstallationContextValue extends PluginInstallationState {
  // Actions
  startInstallation: (
    plugin: Plugin,
    deploymentId?: string,
    context?: Partial<InstallationContext>,
    selectedPreset?: string,
  ) => void;
  setContext: (context: Partial<InstallationContext>) => void;
  openConfigEditor: (instanceId: string, funnelId?: string) => void;
  closeConfigEditor: () => void;
  closeWizard: () => void;
  reset: () => void;
}

const PluginInstallationContext = createContext<PluginInstallationContextValue | null>(null);

const initialState: PluginInstallationState = {
  plugin: null,
  selectedDeploymentId: null,
  selectedPreset: null,
  context: null,
  wizardOpen: false,
  configEditorOpen: false,
  configEditorInstanceId: null,
  targetFunnelId: null,
  shouldNavigateOnClose: false,
  sessionId: '',
};

export function PluginInstallationProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PluginInstallationState>(initialState);

  const startInstallation = useCallback(
    (
      plugin: Plugin,
      deploymentId?: string,
      context?: Partial<InstallationContext>,
      selectedPreset?: string,
    ) => {
      const newSessionId = `install_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      setState((prev) => ({
        ...initialState, // Reset to initial state first
        plugin,
        selectedDeploymentId: deploymentId || plugin.deployments[0]?.deploymentId || null,
        selectedPreset: selectedPreset || 'default',
        context: context
          ? {
              mode: context.funnelId ? 'existing-funnel' : 'new-funnel',
              ...context,
            }
          : null,
        // Only open wizard if no context is provided (user needs to choose funnel/step)
        wizardOpen: !context,
        sessionId: newSessionId,
      }));
    },
    [],
  );

  const setContext = useCallback((context: Partial<InstallationContext>) => {
    setState((prev) => ({
      ...prev,
      context: prev.context ? { ...prev.context, ...context } : ({ ...context } as InstallationContext),
    }));
  }, []);

  const openConfigEditor = useCallback((instanceId: string, funnelId?: string) => {
    setState((prev) => ({
      ...prev,
      configEditorOpen: true,
      configEditorInstanceId: instanceId,
      targetFunnelId: funnelId || prev.targetFunnelId,
      wizardOpen: false,
    }));
  }, []);

  const closeConfigEditor = useCallback(() => {
    setState((prev) => ({
      ...prev,
      configEditorOpen: false,
      configEditorInstanceId: null,
    }));
  }, []);

  const closeWizard = useCallback(() => {
    setState((prev) => ({
      ...prev,
      wizardOpen: false,
    }));
  }, []);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  const value: PluginInstallationContextValue = {
    ...state,
    startInstallation,
    setContext,
    openConfigEditor,
    closeConfigEditor,
    closeWizard,
    reset,
  };

  return <PluginInstallationContext.Provider value={value}>{children}</PluginInstallationContext.Provider>;
}

export function usePluginInstallation() {
  const context = useContext(PluginInstallationContext);
  if (!context) {
    throw new Error('usePluginInstallation must be used within PluginInstallationProvider');
  }
  return context;
}
